import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { PropertyForm } from "../properties/PropertyForm.js";
import { BinderSection } from "../properties/BinderSection.js";
import { InsightsCard } from "../properties/InsightsCard.js";
import { PropertyIconBadge } from "../properties/PropertyIconBadge.js";
import { PropertyIconPicker } from "../properties/PropertyIconPicker.js";
import {
  propertyTypeLabels,
  useDeleteProperty,
  useProperty,
  useUpdateProperty,
} from "../properties/api.js";

export function PropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: property, isLoading, error } = useProperty(id);
  const updateMutation = useUpdateProperty(id ?? "");
  const deleteMutation = useDeleteProperty();
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [changingIcon, setChangingIcon] = useState(false);

  if (!id) return <Navigate to="/app/properties" replace />;
  if (isLoading) return <p className="p-4 text-sm text-slate-500">Loading…</p>;
  if (error || !property) {
    return (
      <div className="p-4">
        <p className="rounded bg-rose-50 p-3 text-sm text-rose-900">Property not found.</p>
        <Link to="/app/properties" className="mt-3 inline-block text-sm text-teal-700">
          Back to properties
        </Link>
      </div>
    );
  }

  if (editing) {
    return (
      <div>
        <header className="border-b border-slate-200 bg-white px-4 py-3">
          <button type="button" onClick={() => setEditing(false)} className="text-sm text-teal-700">
            ‹ Cancel
          </button>
          <h1 className="mt-1 text-xl font-semibold">Edit property</h1>
        </header>
        <PropertyForm
          submitLabel="Save"
          initial={property}
          onSubmit={async (input) => {
            await updateMutation.mutateAsync(input);
            setEditing(false);
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <header className="border-b border-slate-200 bg-white px-4 py-3">
        <Link to="/app/properties" className="text-sm text-teal-700">
          ‹ Properties
        </Link>
        <div className="mt-1 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setChangingIcon(true)}
            className="flex shrink-0 flex-col items-center"
          >
            <PropertyIconBadge property={property} className="h-12 w-12" />
            <span className="mt-0.5 text-[11px] font-medium text-teal-700">Change icon</span>
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-xl font-semibold">{property.nickname}</h1>
            <p className="text-sm text-slate-500">{propertyTypeLabels[property.propertyType]}</p>
          </div>
        </div>
      </header>

      <dl className="space-y-3 p-4 text-sm">
        <Row label="Address">
          <div>
            <div>
              {property.street}
              {property.unit ? `, ${property.unit}` : ""}
            </div>
            <div>
              {property.city}, {property.state} {property.postalCode}
            </div>
          </div>
        </Row>
        {property.notes && <Row label="Notes">{property.notes}</Row>}
      </dl>

      <div className="flex gap-2 px-4 pb-4">
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="min-h-[44px] flex-1 rounded-md border border-slate-300 bg-white text-sm font-medium text-slate-700"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          className="min-h-[44px] flex-1 rounded-md border border-rose-200 bg-white text-sm font-medium text-rose-700"
        >
          Delete
        </button>
      </div>

      <InsightsCard propertyId={property.id} insights={property.insights} />

      <BinderSection propertyId={property.id} />

      {changingIcon && (
        <PropertyIconPicker property={property} onClose={() => setChangingIcon(false)} />
      )}

      {confirmingDelete && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-20 flex items-end bg-slate-900/40 px-4 pb-[max(env(safe-area-inset-bottom),1rem)]"
        >
          <div className="w-full rounded-lg bg-white p-4 shadow-xl">
            <h2 className="text-base font-semibold">Delete this property?</h2>
            <p className="mt-1 text-sm text-slate-600">
              This can&apos;t be undone. RFQs already submitted for it stay in your history.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="min-h-[44px] flex-1 rounded-md border border-slate-300 bg-white text-sm font-medium text-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteMutation.isPending}
                onClick={async () => {
                  await deleteMutation.mutateAsync(property.id);
                  navigate("/app/properties");
                }}
                className="min-h-[44px] flex-1 rounded-md bg-rose-600 text-sm font-medium text-white disabled:opacity-60"
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-slate-900">{children}</dd>
    </div>
  );
}
